import { useState } from "react"
import { useQuery, useMutation } from "urql"
import { Link, useNavigate } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { BookmarksQuery } from "@/lib/graphql/queries"
import { DeleteBookmarkMutation } from "@/lib/graphql/mutations"
import type { ResultOf } from "@/lib/graphql/types"

type BookmarkItem = ResultOf<typeof BookmarksQuery>["bookmarks"]["nodes"][number]

const LIMIT = 25

export function BookmarksPage() {
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [search])

  const [result, reexecute] = useQuery({
    query: BookmarksQuery,
    variables: { search, after: pagination.after, first: pagination.first },
  })
  const [, deleteBookmark] = useMutation(DeleteBookmarkMutation)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const handleDelete = async () => {
    if (!deleteId) return
    const res = await deleteBookmark({ id: deleteId })
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Bookmark removed")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const columns: Column<BookmarkItem>[] = [
    {
      header: "Name",
      cell: (row) => (
        <span className="text-xs font-medium">{row.name || "Untitled"}</span>
      ),
    },
    {
      header: "Event Type",
      cell: (row) => (
        <Link
          to={`/events/${row.event.id}`}
          className="text-xs font-mono hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.event.eventType}
        </Link>
      ),
    },
    {
      header: "Source",
      cell: (row) => (
        <Link
          to={`/sources/${row.event.source.id}`}
          className="text-xs hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.event.source.name}
        </Link>
      ),
    },
    {
      header: "Notes",
      cell: (row) => (
        <span className="text-xs text-muted-foreground max-w-60 truncate block">
          {row.notes || "\u2014"}
        </span>
      ),
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={(e) => { e.stopPropagation(); setDeleteId(row.id) }}
          aria-label="Remove bookmark"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Bookmarks" description="Saved events for quick access" />

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Input
          placeholder="Search bookmarks..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <DataTable
        columns={columns}
        data={result.data?.bookmarks.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.bookmarks.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No bookmarks"
        emptyDescription="Bookmark events to save them for later"
        onRowClick={(row) => navigate(`/events/${row.eventId}`)}
      />

      <ConfirmDialog
        open={deleteId !== null}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Remove Bookmark"
        description="Are you sure you want to remove this bookmark?"
        confirmLabel="Remove"
        onConfirm={handleDelete}
        destructive
      />
    </div>
  )
}
